import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { AuthApi } from "../Api/AuthApi";
import { LoginRequest } from "../Dto/LoginRequest";
import { ResetPasswordRequest } from "../Dto/ResetPasswordRequest";
import { AuthState } from "./AuthState";
import { StorageEffects } from "../../../Common/Storage/State/StorageEffects";
import { StorageKeys } from "../../../Common/Storage/Dto/StorageKeys";
import { getBackendErrorMessage } from "../../../Common/Utility/UtilityError";

@Injectable({ providedIn: "root" })
export class AuthEffects {
  private readonly authApi = inject(AuthApi);
  private readonly authState = inject(AuthState);
  private readonly router = inject(Router);
  private readonly storage = inject(StorageEffects);

  async login(payload: LoginRequest): Promise<void> {
    this.authState.setLoading(true);
    this.authState.setError(null);

    try {
      const loginResponse = await this.authApi.login(payload);
      const meResponse = await this.authApi.me();
      this.authState.setLoggedIn(loginResponse);
      this.authState.setUser(meResponse);

      this.storage.setItem(StorageKeys.AccessToken, loginResponse.accessToken);
      this.storage.setItem(StorageKeys.RefreshToken, loginResponse.refreshToken);

      await this.router.navigate(["/home"]);
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.authState.setError(errorMessage);
    } finally {
      this.authState.setLoading(false);
    }
  }

  async restoreSession(): Promise<boolean> {
    try {
      const response = await this.authApi.me();
      this.authState.setLoggedIn(response);
      this.authState.setUser(response);
      this.storage.setItem(StorageKeys.AccessToken, response.accessToken);
      this.storage.setItem(StorageKeys.RefreshToken, response.refreshToken);
      return true;
    } catch {
      this.authState.setLoggedOut();
      return false;
    }
  }

  async logout(): Promise<void> {
    try {
      await this.authApi.logout();
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.authState.setError(errorMessage);
    } finally {
      this.authState.setLoggedOut();
      this.storage.removeItem(StorageKeys.AccessToken);
      this.storage.removeItem(StorageKeys.RefreshToken);
      await this.router.navigate(["/login"]);
    }
  }

  async resetPassword(payload: ResetPasswordRequest): Promise<{ success: boolean; message: string }> {
    this.authState.setLoading(true);
    this.authState.setError(null);

    try {
      const response = await this.authApi.resetPassword(payload);
      return { success: true, message: response.message };
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.authState.setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      this.authState.setLoading(false);
    }
  }

  async refreshToken(): Promise<void> {
    this.authState.setLoading(true);
    this.authState.setError(null);

    try {
      const refreshToken = this.storage.getItem(StorageKeys.RefreshToken);
      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      const response = await this.authApi.refresh({ refreshToken });
      this.authState.setLoggedIn(response);
      this.storage.setItem(StorageKeys.AccessToken, response.accessToken);
      this.storage.setItem(StorageKeys.RefreshToken, response.refreshToken);
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.authState.setError(errorMessage);
      this.authState.setLoggedOut();
      this.storage.removeItem(StorageKeys.AccessToken);
      this.storage.removeItem(StorageKeys.RefreshToken);
      await this.router.navigate(["/login"]);
    } finally {
      this.authState.setLoading(false);
    }
  }
}
